import appManager from '@app/appManager';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import {
  ChunkLoadFailureReason,
  classifyChunkLoadFailure,
} from './classifyChunkLoadFailure';
import { getChunkLoadErrorUrl } from './isChunkLoadError';

const logger = appManager.logger('ChunkLoad');

/**
 * Failures already reported this page lifetime, keyed by chunk URL (or error
 * message when the browser omits it). A single failed import is seen by both
 * the preload listener and the route that renders the error view, and that
 * view can re-render.
 */
const reported = new Set<string>();

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/**
 * Records a chunk load failure once: increments `chunk_load_error_total`
 * tagged with the reason, and writes a structured log entry that reaches
 * Kibana through the app logger. The log carries the detail that is too
 * high-cardinality for a metric tag (chunk URL, original message).
 *
 * The counter is buffered in memory and flushed periodically, so it is lost if
 * the page reloads first; the log entry is persisted and survives a reload.
 *
 * @returns the classification, so callers can pick matching UI copy.
 */
export const reportChunkLoadFailure = async (
  error: unknown,
): Promise<ChunkLoadFailureReason> => {
  const reason = await classifyChunkLoadFailure(error);

  const url = getChunkLoadErrorUrl(error);
  const key = url ?? getErrorMessage(error);
  if (reported.has(key)) {
    return reason;
  }
  reported.add(key);

  DiagnosticsModule.incrementCounter(MetricNames.CHUNK_LOAD_ERROR_TOTAL, {
    [MetricTags.REASON]: reason,
  });
  logger.warn('Lazy chunk failed to load', {
    reason,
    url,
    message: getErrorMessage(error),
    online: navigator.onLine,
  });

  return reason;
};
